/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {useEffect, useRef, useState} from 'react';

import type {VidyoGetStatsJson, VidyoRoomStats, VidyoTransportInformation} from '@/types/VidyoGetStatsJson';
import {vidyoProviderLogs} from '@/utils/general';

import type {NetworkStats, ParticipantQuality, PrevParticipant, PrevSnapshot} from './types';
import {bpsToKbps, getQualityLevel, safeDelta, scoreParticipant} from './utils';

export const useNetworkQuality = (): NetworkStats => {
    const [stats, setStats] = useState<NetworkStats>({});
    const statsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const prevRef = useRef<PrevSnapshot | null>(null);
    const lastBwLogTimeRef = useRef<number>(0);
    // Keeps the last known good value for every scalar field so a single poll
    // with missing data (undefined RTT delta, counter reset, etc.) does not
    // cause the indicator to jump to "unknown" or "Connecting…".
    // participants is intentionally excluded — their deltas are per-interval.
    const lastKnownRef = useRef<Omit<NetworkStats, 'participants'>>({});

    useEffect(() => {
        const connector = window.vidyoConnector;
        if (!connector) return;

        const fetchStats = async () => {
            try {
                const rawJson: string = await connector.GetStatsJson();
                const parsed = JSON.parse(rawJson) as VidyoGetStatsJson;

                const activeRoom = (parsed?.userStats?.[0]?.roomStats ?? []).find((room: VidyoRoomStats) =>
                    Boolean(room?.participantStats?.length && room.callId),
                );

                if (!activeRoom) return;

                // ── Local camera (declared early — used for RTT fallback and stats) ──
                const localCamera = parsed?.localCameraStats?.[0];

                // ── RTT: totalRoundTripTime is CUMULATIVE seconds → use delta ──
                const transport: VidyoTransportInformation | undefined = activeRoom.transportInformation?.[0];
                const currentRttCumSec = transport?.totalRoundTripTime ?? 0;
                const prevRttCumSec = prevRef.current?.rttCumSec;
                let rttMs: number | undefined;
                if (prevRttCumSec !== undefined && currentRttCumSec > prevRttCumSec) {
                    rttMs = Math.round((currentRttCumSec - prevRttCumSec) * 1000);
                } else {
                    // First poll or reset — fallback: sendNetworkRtt from local camera renderer (nanoseconds)
                    const rttNs = localCamera?.remoteRendererStreams?.find(
                        (s: any) => (s.height ?? 0) > 0,
                    )?.sendNetworkRtt;
                    if (rttNs) rttMs = Math.round(rttNs / 1_000_000);
                }

                // ── Video codec from first active remote camera ──
                const remoteCamera = activeRoom.participantStats.flatMap((p) => p.remoteCameraStats).find(Boolean);

                // ── Rate-shaper packet drop % ──
                const shaperVideo = activeRoom.rateShaperVideo;
                const videoDropPct =
                    shaperVideo?.packetsNormal > 0
                        ? (shaperVideo.dropNormal / shaperVideo.packetsNormal) * 100
                        : undefined;

                // ── Audio: sendNetworkRtt is in nanoseconds → divide by 1_000_000 for ms ──
                const audioStream = parsed?.localMicrophoneStats?.[0]?.remoteSpeakerStreams?.[0];
                const audioRttMs = audioStream?.sendNetworkRtt
                    ? Math.round(audioStream.sendNetworkRtt / 1_000_000)
                    : undefined;

                // ── Per-participant quality with delta packet counts ──
                const prevParticipants = prevRef.current?.participants ?? new Map<string, PrevParticipant>();
                const nextParticipants = new Map<string, PrevParticipant>();

                const participants: ParticipantQuality[] = activeRoom.participantStats
                    // Filter self-reflection: local user is mirrored back with empty streams
                    .filter((p) => (p.remoteCameraStats?.length ?? 0) > 0 || (p.remoteMicrophoneStats?.length ?? 0) > 0)
                    .map((p) => {
                        const cam = p.remoteCameraStats?.[0];
                        const mic = p.remoteMicrophoneStats?.[0];
                        const prev = prevParticipants.get(p.id);

                        // Save raw cumulative values for next poll's delta computation
                        nextParticipants.set(p.id, {
                            videoPacketsLost: cam?.receiveNetworkPacketsLost ?? 0,
                            videoPacketsConcealed: cam?.receiveNetworkPacketsConcealed ?? 0,
                            videoPacketsReordered: cam?.receiveNetworkPacketsReordered ?? 0,
                            micPacketsLost: mic?.receiveNetworkPacketsLost ?? 0,
                            micPacketsConcealed: mic?.receiveNetworkPacketsConcealed ?? 0,
                        });

                        const data: Omit<ParticipantQuality, 'quality'> = {
                            id: p.id,
                            name: p.name,
                            videoRxBitRate: cam?.receiveNetworkBitRate,
                            videoPacketsLost: safeDelta(cam?.receiveNetworkPacketsLost, prev?.videoPacketsLost),
                            videoPacketsConcealed: safeDelta(
                                cam?.receiveNetworkPacketsConcealed,
                                prev?.videoPacketsConcealed,
                            ),
                            videoPacketsReordered: safeDelta(
                                cam?.receiveNetworkPacketsReordered,
                                prev?.videoPacketsReordered,
                            ),
                            videoCodec: cam?.codecName || undefined,
                            videoFpsDecoded: cam?.fpsDecoded,
                            micRxBitRate: mic?.receiveNetworkBitRate,
                            micPacketsLost: safeDelta(mic?.receiveNetworkPacketsLost, prev?.micPacketsLost),
                            micPacketsConcealed: safeDelta(
                                mic?.receiveNetworkPacketsConcealed,
                                prev?.micPacketsConcealed,
                            ),
                            micJitter: mic?.receiveNetworkJitter, // real-time seconds, no delta
                            micCodec: mic?.codecName || undefined,
                        };

                        // On first poll prev is undefined → deltas are undefined → scores as 'good'
                        return {...data, quality: scoreParticipant(data)};
                    });

                // Save snapshot for next poll
                prevRef.current = {rttCumSec: currentRttCumSec, participants: nextParticipants};

                const newStats: NetworkStats = {
                    receiveBitRateAvailable: bpsToKbps(activeRoom.receiveBitRateAvailable),
                    receiveBitRateTotal: bpsToKbps(activeRoom.receiveBitRateTotal),
                    sendBitRateAvailable: bpsToKbps(activeRoom.sendBitRateAvailable),
                    sendBitRateTotal: bpsToKbps(activeRoom.sendBitRateTotal),
                    availableDecodeBwPercent: activeRoom.availableDecodeBwPercent,
                    availableEncodeBwPercent: activeRoom.availableEncodeBwPercent,
                    videoActualBitRate: bpsToKbps(activeRoom.bandwidthVideo?.actualEncoderBitRate),
                    videoTargetBitRate: bpsToKbps(activeRoom.bandwidthVideo?.targetEncoderBitRate),
                    videoDropPct,
                    videoCodec: remoteCamera?.codecName || undefined,
                    audioActualBitRate: bpsToKbps(activeRoom.bandwidthAudio?.actualEncoderBitRate),
                    audioCodec: audioStream?.codecName || undefined,
                    audioRtt: audioRttMs,
                    rtt: rttMs,
                    transport: transport?.transportPlugIn || undefined,
                    localVideoFps: localCamera?.remoteRendererStreams?.[0]?.fpsSent,
                    localQualityLimit:
                        localCamera?.qualityLimitationReason !== 'none'
                            ? localCamera?.qualityLimitationReason
                            : undefined,
                    participants,
                };

                // Fill in any undefined scalar fields from the last known good values
                const last = lastKnownRef.current;
                const stickyStats: NetworkStats = {
                    receiveBitRateAvailable: newStats.receiveBitRateAvailable ?? last.receiveBitRateAvailable,
                    receiveBitRateTotal: newStats.receiveBitRateTotal ?? last.receiveBitRateTotal,
                    sendBitRateAvailable: newStats.sendBitRateAvailable ?? last.sendBitRateAvailable,
                    sendBitRateTotal: newStats.sendBitRateTotal ?? last.sendBitRateTotal,
                    availableDecodeBwPercent: newStats.availableDecodeBwPercent ?? last.availableDecodeBwPercent,
                    availableEncodeBwPercent: newStats.availableEncodeBwPercent ?? last.availableEncodeBwPercent,
                    videoActualBitRate: newStats.videoActualBitRate ?? last.videoActualBitRate,
                    videoTargetBitRate: newStats.videoTargetBitRate ?? last.videoTargetBitRate,
                    videoDropPct: newStats.videoDropPct ?? last.videoDropPct,
                    videoCodec: newStats.videoCodec ?? last.videoCodec,
                    audioActualBitRate: newStats.audioActualBitRate ?? last.audioActualBitRate,
                    audioCodec: newStats.audioCodec ?? last.audioCodec,
                    audioRtt: newStats.audioRtt ?? last.audioRtt,
                    rtt: newStats.rtt ?? last.rtt,
                    transport: newStats.transport ?? last.transport,
                    localVideoFps: newStats.localVideoFps ?? last.localVideoFps,
                    localQualityLimit: newStats.localQualityLimit ?? last.localQualityLimit,
                    participants: newStats.participants,
                };
                lastKnownRef.current = stickyStats;
                setStats(stickyStats);

                // Log bandwidth stats every 10 seconds
                const now = Date.now();
                if (now - lastBwLogTimeRef.current >= 10_000) {
                    lastBwLogTimeRef.current = now;

                    const participantLog = participants
                        .map(
                            (p) =>
                                `[${p.name} q:${p.quality}` +
                                (p.videoRxBitRate != null ? ` vBR:${Math.round(p.videoRxBitRate / 1000)}kbps` : '') +
                                (p.videoPacketsLost != null ? ` vLost:${p.videoPacketsLost}Δ` : '') +
                                (p.videoPacketsConcealed != null ? ` vConc:${p.videoPacketsConcealed}Δ` : '') +
                                (p.micRxBitRate != null ? ` aBR:${Math.round(p.micRxBitRate / 1000)}kbps` : '') +
                                (p.micPacketsLost != null ? ` aLost:${p.micPacketsLost}Δ` : '') +
                                (p.micPacketsConcealed != null ? ` aConc:${p.micPacketsConcealed}Δ` : '') +
                                (p.micJitter != null ? ` aJitter:${(p.micJitter * 1000).toFixed(0)}ms` : '') +
                                ']',
                        )
                        .join(' ');

                    const qualityReasons: string[] = [];
                    const qualityVerdict = getQualityLevel(stickyStats, qualityReasons);
                    if (qualityReasons.length > 0) {
                        vidyoProviderLogs({
                            message: `quality degraded to "${qualityVerdict}" — ${qualityReasons.join(', ')}`,
                            eventType: 'bandwidth',
                            status: 'warn',
                        });
                    }

                    vidyoProviderLogs({
                        message:
                            `quality: ${qualityVerdict}` +
                            ` | recv: ${activeRoom.receiveBitRateAvailable ?? '—'}/${activeRoom.receiveBitRateTotal ?? '—'} bps` +
                            ` | send: ${activeRoom.sendBitRateAvailable ?? '—'}/${activeRoom.sendBitRateTotal ?? '—'} bps` +
                            ` | decodeBw: ${activeRoom.availableDecodeBwPercent ?? '—'}%` +
                            ` | encodeBw: ${activeRoom.availableEncodeBwPercent ?? '—'}%` +
                            ` | videoActualBR: ${activeRoom.bandwidthVideo?.actualEncoderBitRate ?? '—'} bps` +
                            ` | audioActualBR: ${activeRoom.bandwidthAudio?.actualEncoderBitRate ?? '—'} bps` +
                            ` | rtt: ${rttMs ?? '—'} ms` +
                            ` | transport: ${transport?.transportPlugIn ?? '—'}` +
                            ` | pktDrop: ${videoDropPct !== undefined ? videoDropPct.toFixed(2) : '—'}%` +
                            ` | localFps: ${localCamera?.remoteRendererStreams?.[0]?.fpsSent ?? '—'}` +
                            ` | qualityLimit: ${localCamera?.qualityLimitationReason ?? '—'}` +
                            (participantLog ? ` | participants: ${participantLog}` : ''),
                        eventType: 'bandwidth',
                        status: 'info',
                    });
                }
            } catch (err) {
                vidyoProviderLogs({
                    message: 'GetStatsJson failed: ' + String(err),
                    eventType: 'bandwidth',
                    status: 'error',
                });
            }
        };

        void fetchStats();
        statsIntervalRef.current = setInterval(() => void fetchStats(), 2000);

        return () => {
            if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
        };
    }, []);

    return stats;
};
